import {
  isContentCoveragePropertyName,
  isElementSbefProperty,
} from '@shared/templates/categories/common-element-category-properties.helpers';
import type {
  IElementProperty,
  IFactoryProperty,
} from '../models/element-property.model';
import { ElementKind, OneOfPropertyElements } from '../models/project.model';
import { getElementCategory } from './element-category.helpers';
import { requiredKind } from './element-kind.helpers';
import {
  getElementQuantityFormattedName,
  isElementQuantityProperty,
} from './element-quantity-core.helpers';
import {
  isFullyEditableProperty,
  isIconElementProperty,
} from './element-property.helpers';
import { labelFromValue } from './string.helpers';

/**
 * Label for optional-property menu rows (factory template or preview instance).
 */
export const getAddPropertyMenuLabel = (
  property: IElementProperty | IFactoryProperty,
): string => {
  if (isContentCoveragePropertyName(property.name)) {
    return 'Content coverage';
  }
  if ('id' in property && property.id) {
    return getPropertyDisplayName(property as IElementProperty);
  }
  if (property.displayName) {
    return property.displayName;
  }
  if (property.name) {
    return labelFromValue(property.name);
  }
  return 'Property';
};

/**
 * Display name for a property (category quantity labels, SBEF, etc.).
 */
export const getPropertyDisplayName = (
  property: IElementProperty,
  element?: OneOfPropertyElements,
): string => {
  const { name, displayName } = property;

  if (isElementSbefProperty(property)) {
    return 'SBEF Code';
  }

  if (isContentCoveragePropertyName(name)) {
    return 'Content coverage';
  }

  if (isElementQuantityProperty(property)) {
    const category = getElementCategory(property);
    const categoryLabel = category?.getQuantityPropertyLabel?.(
      property,
      requiredKind(element, ElementKind.Element),
    );

    return (
      categoryLabel ?? displayName ?? getElementQuantityFormattedName(name)
    );
  }

  if (property.displayName) {
    return property.displayName;
  }

  return isFullyEditableProperty(property) ? name : labelFromValue(name);
};

const PROPERTY_NAME_MAX_LENGTH = 15;
const PROPERTY_NAME_AND_COUNT_MAX_LENGTH = 35;

const nameAndCountTooLong = (property: IElementProperty): boolean => {
  return (
    typeof property.count === 'string' &&
    property.name.length + property.count.length >
      PROPERTY_NAME_AND_COUNT_MAX_LENGTH
  );
};

/**
 * Tooltip for property name when the raw name is truncated in the UI.
 */
export const getPropertyNameTooltip = (
  property: IElementProperty,
  element: OneOfPropertyElements,
): string => {
  if (property.description) {
    return property.description;
  }
  if (isIconElementProperty(property)) {
    return getPropertyDisplayName(property, element);
  }
  const nameTooLong = property.name.length > PROPERTY_NAME_MAX_LENGTH;

  return nameTooLong && nameAndCountTooLong(property)
    ? getPropertyDisplayName(property)
    : '';
};
